import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, user } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const processedRef = useRef(false);
  const sessionIdRef = useRef(null);

  useEffect(() => {
    // If already logged in, redirect to app
    if (user) {
      navigate('/app', { replace: true });
      return;
    }

    // Prevent double processing
    if (processedRef.current) {
      return;
    }

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    
    if (!match) {
      setStatus('error');
      setErrorMessage('No session ID received from Google. Please try again.');
      return;
    }

    const sessionId = match[1];
    
    // Check if we already processed this exact session
    if (sessionIdRef.current === sessionId) {
      return;
    }
    
    sessionIdRef.current = sessionId;
    processedRef.current = true;

    // Process the OAuth
    const doAuth = async () => {
      try {
        console.log('Processing Google OAuth session...');
        const userData = await loginWithGoogle(sessionId);
        console.log('Google OAuth successful:', userData.email);
        setStatus('success');
        
        // Clear hash and redirect
        window.history.replaceState(null, '', window.location.pathname);
        
        // Small delay then redirect
        setTimeout(() => {
          navigate('/app', { replace: true });
        }, 500);
        
      } catch (error) {
        console.error('Google OAuth error:', error);
        processedRef.current = false; // Allow retry
        sessionIdRef.current = null;
        
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        
        if (detail.includes('expired') || detail.includes('not found') || detail.includes('Invalid')) {
          setErrorMessage('Session expired. Please try signing in again.');
        } else if (detail.includes('timeout')) {
          setErrorMessage('Connection timeout. Please check your internet and try again.');
        } else {
          setErrorMessage(detail);
        }
        setStatus('error');
      }
    };

    doAuth();
  }, [user, navigate, loginWithGoogle]);

  const handleRetry = () => {
    // Go back to login and try again
    navigate('/login', { replace: true });
  };

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center px-4 safe-bottom">
        <div className="text-center max-w-sm w-full">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-400 mb-2">認證失敗</p>
          <p className="text-red-400 text-sm mb-6 px-4">{errorMessage}</p>
          
          <Button
            onClick={handleRetry}
            className="w-full h-12 bg-fac-green hover:bg-fac-green/90 text-black font-bold rounded-lg"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Try Again / 重試
          </Button>
          
          <p className="text-gray-500 text-xs mt-4">
            If this keeps happening, try using email login instead.
            <br />
            如果此問題持續發生，請嘗試使用電子郵件登入。
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-fac-green rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-white text-lg font-medium">Login Successful!</p>
          <p className="text-gray-400">登入成功！</p>
          <p className="text-gray-500 text-sm mt-2">Redirecting... / 正在跳轉...</p>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="min-h-screen bg-fac-dark flex items-center justify-center px-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-fac-green animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Signing you in...</p>
        <p className="text-gray-400">登入中...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
